import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {Store} from '@ngxs/store';
import {TrackAsyncRequest} from 'app/blocks/store/actions/common-actions';

export interface BulkOperation {
    id: string;
    operationName: string;
    runIds: number[];
    timestamp: Date;
    userId?: number;
    tenantId?: string;
    status: 'in_progress' | 'completed' | 'failed';
    successCount?: number;
    failureCount?: number;
    totalCount?: number;
}

@Injectable({
    providedIn: 'root'
})
export class BulkOperationService {
    private activeBulkOperations$ = new BehaviorSubject<BulkOperation[]>([]);
    private isBulkOperationInProgress$ = new BehaviorSubject<boolean>(false);
    private globallyBlockedRunIds$ = new BehaviorSubject<Set<number>>(new Set());

    constructor(private store: Store) {}

    /**
     * Get observable for active bulk operations
     */
    getActiveBulkOperations(): Observable<BulkOperation[]> {
        return this.activeBulkOperations$.asObservable();
    }

    /**
     * Get observable for bulk operation in progress status
     */
    isBulkOperationInProgress(): Observable<boolean> {
        return this.isBulkOperationInProgress$.asObservable();
    }

    /**
     * Get observable for globally blocked run IDs
     */
    getGloballyBlockedRunIds(): Observable<Set<number>> {
        return this.globallyBlockedRunIds$.asObservable();
    }

    /**
     * Get current value of globally blocked run IDs
     */
    getGloballyBlockedRunIdsValue(): Set<number> {
        return this.globallyBlockedRunIds$.value;
    }

    /**
     * Check if any of the given run IDs are globally blocked
     */
    areRunIdsGloballyBlocked(runIds: number[]): boolean {
        const blockedRunIds = this.globallyBlockedRunIds$.value;
        console.log('blockedRunIds', blockedRunIds);
        return runIds.some((id) => blockedRunIds.has(id));
    }

    /**
     * Add run IDs to global blocking
     */
    addRunIdsToGlobalBlocking(runIds: number[]): void {
        const currentBlockedRunIds = this.globallyBlockedRunIds$.value;
        const updatedBlockedRunIds = new Set([...currentBlockedRunIds, ...runIds]);
        this.globallyBlockedRunIds$.next(updatedBlockedRunIds);

        // Clean up stale run IDs after adding new ones
        this.cleanupStaleRunIds();
    }

    /**
     * Remove run IDs from global blocking
     */
    removeRunIdsFromGlobalBlocking(runIds: number[] | null): void {
        console.log('remove run ids', runIds);
        const currentBlockedRunIds = this.globallyBlockedRunIds$.value;
        const updatedBlockedRunIds = new Set(currentBlockedRunIds);
        if (runIds) {
            runIds.forEach((id) => updatedBlockedRunIds.delete(id));
        }
        this.globallyBlockedRunIds$.next(updatedBlockedRunIds);
    }

    /**
     * Start a new bulk operation
     */
    startBulkOperation(operationName: string, runIds: number[], userId?: number, tenantId?: string): string {
        const operationId = this.generateOperationId();
        const newOperation: BulkOperation = {
            id: operationId,
            operationName,
            runIds,
            timestamp: new Date(),
            userId,
            tenantId,
            status: 'in_progress'
        };

        const currentOperations = this.activeBulkOperations$.value;
        const updatedOperations = [...currentOperations, newOperation];
        this.activeBulkOperations$.next(updatedOperations);
        this.updateBulkOperationStatus();

        this.addRunIdsToGlobalBlocking(runIds);

        // Start tracking async requests
        this.store.dispatch(new TrackAsyncRequest());

        return operationId;
    }

    /**
     * Complete a bulk operation
     */
    completeBulkOperation(operationId: string, successCount: number, failureCount: number, totalCount: number): void {
        const currentOperations = this.activeBulkOperations$.value;
        const updatedOperations = currentOperations.map((operation) => {
            if (operation.id === operationId) {
                return {
                    ...operation,
                    status: 'completed' as const,
                    successCount,
                    failureCount,
                    totalCount
                };
            }
            return operation;
        });

        this.activeBulkOperations$.next(updatedOperations);
        this.updateBulkOperationStatus();

        const completedOperation = currentOperations.find((op) => op.id === operationId);
        if (completedOperation) {
            this.removeRunIdsFromGlobalBlocking(completedOperation.runIds);
        }

        // Clear completed operations after 10 seconds
        this.clearCompletedOperationsAfterDelay(10000);
    }

    /**
     * Fail a bulk operation
     */
    failBulkOperation(operationId: string): void {
        const currentOperations = this.activeBulkOperations$.value;
        const updatedOperations = currentOperations.map((operation) => {
            if (operation.id === operationId) {
                return {
                    ...operation,
                    status: 'failed' as const
                };
            }
            return operation;
        });

        this.activeBulkOperations$.next(updatedOperations);
        this.updateBulkOperationStatus();

        // Remove run IDs from global blocking
        const failedOperation = currentOperations.find((op) => op.id === operationId);
        if (failedOperation) {
            this.removeRunIdsFromGlobalBlocking(failedOperation.runIds);
        }

        // Clear failed operations after 10 seconds
        this.clearCompletedOperationsAfterDelay(10000);
    }

    /**
     * Remove a completed/failed bulk operation
     */
    removeBulkOperation(operationId: string): void {
        const currentOperations = this.activeBulkOperations$.value;
        const updatedOperations = currentOperations.filter((operation) => operation.id !== operationId);
        this.activeBulkOperations$.next(updatedOperations);
        this.updateBulkOperationStatus();
    }

    /**
     * Check if any bulk operation is currently in progress
     */
    hasActiveBulkOperation(): boolean {
        return this.activeBulkOperations$.value.some((operation) => operation.status === 'in_progress');
    }

    /**
     * Get all active bulk operations
     */
    getActiveBulkOperationsValue(): BulkOperation[] {
        return this.activeBulkOperations$.value;
    }

    /**
     * Clear all completed/failed operations
     */
    clearCompletedOperations(): void {
        const currentOperations = this.activeBulkOperations$.value;
        const activeOperations = currentOperations.filter((operation) => operation.status === 'in_progress');
        this.activeBulkOperations$.next(activeOperations);
        this.updateBulkOperationStatus();
    }

    /**
     * Clear completed operations after a delay
     */
    clearCompletedOperationsAfterDelay(delayMs: number = 10000): void {
        setTimeout(() => {
            this.clearCompletedOperations();
        }, delayMs);
    }

    /**
     * Start periodic cleanup of stale operations (every 5 minutes)
     */
    startPeriodicCleanup(): void {
        // Clean up stale operations every 5 minutes
        setInterval(
            () => {
                this.cleanupStaleInProgressOperations();
                this.cleanupStaleRunIds();
            },
            5 * 60 * 1000
        ); // 5 minutes
    }

    /**
     * Clean up stale run IDs from global blocking (older than 15 minutes)
     */
    cleanupStaleRunIds(): void {
        const currentTime = new Date();
        const fifteenMinutesAgo = new Date(currentTime.getTime() - 15 * 60 * 1000);

        // Get all in-progress operations
        const inProgressOperations = this.activeBulkOperations$.value.filter((operation) => operation.status === 'in_progress');

        // Get all run IDs that should still be blocked (from recent operations)
        const validBlockedRunIds = new Set<number>();
        inProgressOperations.forEach((operation) => {
            if (operation.timestamp > fifteenMinutesAgo) {
                // Operation is recent, keep its run IDs blocked
                operation.runIds.forEach((id) => validBlockedRunIds.add(id));
            }
        });

        // Update the globally blocked run IDs to only include recent ones
        const currentBlockedRunIds = this.globallyBlockedRunIds$.value;
        if (this.setsAreEqual(currentBlockedRunIds, validBlockedRunIds)) {
            return; // No change needed
        }

        this.globallyBlockedRunIds$.next(validBlockedRunIds);
        console.log(`Cleaned up stale run IDs. Remaining blocked: ${validBlockedRunIds.size}`);
    }

    /**
     * Clean up stale in-progress operations (older than 15 minutes)
     */
    cleanupStaleInProgressOperations(): void {
        const currentTime = new Date();
        const fifteenMinutesAgo = new Date(currentTime.getTime() - 15 * 60 * 1000);

        const currentOperations = this.activeBulkOperations$.value;
        const updatedOperations = currentOperations.filter((operation) => {
            // Keep operations that are either completed/failed or recent (within 15 minutes)
            if (operation.status !== 'in_progress') {
                return true; // Keep completed/failed operations
            }

            const isRecent = operation.timestamp > fifteenMinutesAgo;
            if (!isRecent) {
                // Remove stale operation from global blocking
                this.removeRunIdsFromGlobalBlocking(operation.runIds);
                console.log(`Cleaned up stale operation: ${operation.operationName} (${operation.id})`);
            }

            return isRecent; // Only keep recent in-progress operations
        });

        if (updatedOperations.length !== currentOperations.length) {
            this.activeBulkOperations$.next(updatedOperations);
            this.updateBulkOperationStatus();
        }
    }

    /**
     * Update the bulk operation status
     */
    private updateBulkOperationStatus(): void {
        const hasActiveOperation = this.hasActiveBulkOperation();
        this.isBulkOperationInProgress$.next(hasActiveOperation);
    }

    /**
     * Generate a unique operation ID
     */
    private generateOperationId(): string {
        return `bulk_op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Check if two sets are equal
     */
    private setsAreEqual(set1: Set<number>, set2: Set<number>): boolean {
        if (set1.size !== set2.size) {
            return false;
        }
        for (const item of set1) {
            if (!set2.has(item)) {
                return false;
            }
        }
        return true;
    }
}
